/**
 * MoveToDialog Component
 *
 * A dialog with a FolderSelect for picking a destination folder.
 * Used for moving both templates and folders to a new location.
 */

import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Loader2 } from 'lucide-react'
import { FolderSelect } from './FolderSelect'
import type { EmailFolder } from '@/lib/email-templates/folder-types'

interface MoveToDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  folders: EmailFolder[]
  /** The item being moved */
  itemName: string
  /** Whether we're moving a folder or a template */
  itemType: 'folder' | 'template'
  /** The folder ID to exclude from the dropdown (the item's own ID, if moving a folder) */
  excludeFolderId?: number | null
  onSubmit: (targetFolderId: number | null) => Promise<void>
  isSubmitting: boolean
}

export function MoveToDialog({
  open,
  onOpenChange,
  folders,
  itemName,
  itemType,
  excludeFolderId,
  onSubmit,
  isSubmitting,
}: MoveToDialogProps) {
  const [targetFolderId, setTargetFolderId] = useState<number | null>(null)

  // Reset when dialog opens
  useEffect(() => {
    if (open) {
      setTargetFolderId(null)
    }
  }, [open])

  // Filter out the folder being moved (and its descendants) from the options
  const availableFolders = excludeFolderId
    ? filterOutDescendants(folders, excludeFolderId)
    : folders

  const handleSubmit = async () => {
    await onSubmit(targetFolderId)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Move {itemType === 'folder' ? 'Folder' : 'Template'}</DialogTitle>
          <DialogDescription>
            Choose a new location for <strong>{itemName}</strong>.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Destination Folder</Label>
            <FolderSelect
              folders={availableFolders}
              value={targetFolderId}
              onChange={setTargetFolderId}
              placeholder="(Root level)"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Moving...
              </>
            ) : (
              'Move'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/**
 * Filters out a folder and all its descendants from the list.
 * Prevents moving a folder into itself or its own children.
 */
function filterOutDescendants(folders: EmailFolder[], excludeId: number): EmailFolder[] {
  const excludeIds = new Set<number>([excludeId])

  // Iteratively find all descendants
  let changed = true
  while (changed) {
    changed = false
    for (const folder of folders) {
      if (folder.parentId && excludeIds.has(folder.parentId) && !excludeIds.has(folder.id)) {
        excludeIds.add(folder.id)
        changed = true
      }
    }
  }

  return folders.filter((f) => !excludeIds.has(f.id))
}
